import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Printer, ArrowLeft, Mail, Phone, Globe, MapPin, Pencil, Save, X } from 'lucide-react';

/* ─── tiny helper ─────────────────────────────────────────────── */
const formatCurrency = (n) =>
    new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

/* ─── editable field primitives (invisible border until hover/focus) ─ */
const EInput = ({ value, onChange, className = '', style = {}, type = 'text' }) =>
    <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        className={className}
        style={{
            background: 'transparent',
            border: 'none',
            borderBottom: '1.5px dashed #94a3b8',
            outline: 'none',
            width: '100%',
            fontFamily: 'inherit',
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            lineHeight: 'inherit',
            padding: '1px 2px',
            ...style,
        }}
    />;

const EArea = ({ value, onChange, rows = 3, className = '' }) =>
    <textarea
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        className={className}
        style={{
            background: 'transparent',
            border: '1.5px dashed #94a3b8',
            borderRadius: 8,
            outline: 'none',
            width: '100%',
            fontFamily: 'inherit',
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            padding: '4px 6px',
            resize: 'vertical',
        }}
    />;

/* ─── deep-clone a quote into editable form state ─────────────── */
const toForm = (q) => ({
    // company header (static per-company, kept editable locally for print override)
    company_name: 'Techzon IT Solutions',
    company_tagline: 'Excellence in Technology',
    company_address: 'Office 1204, Aspect Tower, Business Bay, Dubai',
    company_phone: '+971 4 123 4567',
    company_email: 'info@techzonme.com',
    company_website: 'www.techzonme.com',
    footer_note: 'This is a computer generated document. No signature is required unless requested.',
    // quote fields
    quote_number: q.quote_number || '',
    date: q.date || q.created_at || '',
    customer_name: q.customer_id?.company_name || q.customer_name || '',
    contact_person: q.customer_id?.contact_person || q.contact_person || '',
    customer_address: q.customer_id?.address || '',
    executive_name: q.executive_id?.name || '',
    executive_designation: q.executive_id?.designation || 'Key Account Manager',
    executive_email: q.executive_id?.email || 'sales@techzonme.com',
    terms: q.terms || '100% Advance Payment. Validity 30 Days.',
    items: (q.items || []).map(it => ({
        id: it.id || it._id,
        name: it.item_id?.name || 'Standard Service',
        description: it.item_id?.description || '',
        qty: it.qty ?? 1,
        rate: it.rate ?? 0,
        amount: it.amount ?? (it.qty * it.rate),
    })),
});

/* ═══════════════════════════════════════════════════════════════ */
const QuotationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchQuoteDetails = async () => {
            try {
                const res = await api.get(`/quotes/${id}`);
                const q = res.data.data;
                setQuote(q);
                setForm(toForm(q));
            } catch (err) {
                console.error('Error fetching quote details:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchQuoteDetails();
    }, [id]);

    /* ── field helpers ── */
    const setField = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

    const setItemField = (idx, key) => (val) => {
        setForm(f => {
            const items = f.items.map((it, i) => {
                if (i !== idx) return it;
                const updated = { ...it, [key]: val };
                if (key === 'qty' || key === 'rate') {
                    updated.amount = (Number(key === 'qty' ? val : updated.qty) || 0)
                        * (Number(key === 'rate' ? val : updated.rate) || 0);
                }
                return updated;
            });
            return { ...f, items };
        });
    };

    /* ── save ── */
    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                terms: form.terms,
                items: form.items.map(it => ({
                    id: it.id,
                    qty: Number(it.qty),
                    rate: Number(it.rate),
                    amount: Number(it.amount),
                })),
            };
            await api.patch(`/quotes/${id}`, payload);
            setIsEditing(false);
        } catch (err) {
            console.error('Save failed:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setForm(toForm(quote));
        setIsEditing(false);
    };

    /* ── derived totals ── */
    const subtotal = form ? form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0) : 0;
    const vat = subtotal * 0.05;
    const grandTotal = subtotal + vat;

    const issuedDate = form ? new Date(form.date) : new Date();
    const validDate = new Date(issuedDate.getTime() + 30 * 24 * 60 * 60 * 1000);

    /* ── guards ── */
    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );

    if (!quote || !form) return (
        <div className="p-10 text-center bg-slate-50 min-h-screen">
            <h2 className="text-2xl font-bold text-red-600">Quotation not found</h2>
            <button onClick={() => navigate('/dashboard')} className="mt-4 text-primary underline italic flex items-center justify-center gap-2 mx-auto">
                <ArrowLeft size={16} /> Back to Dashboard
            </button>
        </div>
    );

    /* shorthand for rendering read vs edit */
    const F = (key, display, className = '') => isEditing
        ? <EInput value={form[key]} onChange={setField(key)} className={className} />
        : <span className={className}>{display ?? form[key]}</span>;

    return (
        <div className="min-h-screen bg-slate-100 pb-20 font-sans antialiased text-slate-800">

            {/* ── Toolbar ──────────────────────────────────────── */}
            <div className="print:hidden bg-white/80 backdrop-blur-md border-b sticky top-0 z-50 shadow-sm">
                <div className="max-w-[900px] mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="flex items-center gap-2 text-slate-500 hover:text-primary transition-all font-bold text-sm uppercase tracking-widest"
                    >
                        <ArrowLeft size={18} /> Exit Preview
                    </button>

                    <div className="flex gap-3">
                        {!isEditing ? (
                            <>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="flex items-center gap-2 bg-slate-800 text-white px-6 py-3 rounded-full font-black text-sm hover:scale-105 transition-all shadow-lg"
                                >
                                    <Pencil size={15} /> Edit
                                </button>
                                <button
                                    onClick={() => window.print()}
                                    className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-black text-sm hover:scale-105 transition-all shadow-xl shadow-primary/20"
                                >
                                    <Printer size={18} /> Print Document
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={handleCancel}
                                    className="flex items-center gap-2 border-2 border-slate-300 text-slate-600 px-6 py-3 rounded-full font-black text-sm hover:bg-slate-100 transition-all"
                                >
                                    <X size={15} /> Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex items-center gap-2 bg-emerald-600 text-white px-8 py-3 rounded-full font-black text-sm hover:scale-105 transition-all shadow-lg shadow-emerald-600/20 disabled:opacity-60"
                                >
                                    <Save size={15} /> {saving ? 'Saving…' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ── A4 Page ──────────────────────────────────────── */}
            <div className="print-area max-w-[210mm] mx-auto mt-6 mb-10 bg-white shadow-[0_0_50px_rgba(0,0,0,0.1)] p-[20mm] print:p-0 print:m-0 print:shadow-none print:w-[210mm] print:h-[297mm] flex flex-col box-border overflow-hidden relative">

                {/* 1. Header */}
                <div className="flex justify-between items-start border-b-2 border-primary pb-6 mb-6">
                    <div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="bg-primary text-white w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30 shrink-0">
                                <span className="text-2xl font-black italic">TZ</span>
                            </div>
                            <div>
                                <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase">
                                    {F('company_name')}
                                </h1>
                                <p className="text-xs font-bold text-accent tracking-[0.3em] uppercase opacity-80 mt-1">
                                    {F('company_tagline')}
                                </p>
                            </div>
                        </div>
                        <div className="space-y-1 text-xs text-slate-500 font-bold leading-tight">
                            <div className="flex items-center gap-2"><MapPin size={11} className="text-primary shrink-0" />{F('company_address')}</div>
                            <div className="flex items-center gap-2"><Phone size={11} className="text-primary shrink-0" />{F('company_phone')}</div>
                            <div className="flex items-center gap-2"><Mail size={11} className="text-primary shrink-0" />{F('company_email')}</div>
                            <div className="flex items-center gap-2"><Globe size={11} className="text-primary shrink-0" />{F('company_website')}</div>
                        </div>
                    </div>

                    <div className="text-right">
                        <div className="mb-8">
                            <h2 className="text-6xl font-black text-slate-100 uppercase leading-none tracking-tighter select-none">QUOTATION</h2>
                            <div className="bg-primary text-white text-[10px] font-black tracking-[0.5em] px-4 py-1 inline-block mt-[-10px] relative z-10 uppercase italic">
                                Ref: {F('quote_number')}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-end gap-10">
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Date Issued</p>
                                    {isEditing
                                        ? <EInput value={form.date?.split('T')[0] ?? ''} onChange={setField('date')} type="date" style={{ color: '#0f172a', fontWeight: 900, fontSize: '0.875rem' }} />
                                        : <p className="text-sm font-black text-slate-900">{issuedDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                    }
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Valid Until</p>
                                    <p className="text-sm font-black text-slate-900">{validDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Recipient */}
                <div className="grid grid-cols-2 gap-12 mb-6 break-inside-avoid">
                    <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-3 italic">Quotation Prepared For</p>
                        <h3 className="text-xl font-black text-slate-900 leading-tight mb-1 uppercase">
                            {F('customer_name')}
                        </h3>
                        <p className="text-xs font-bold text-slate-500 italic mb-3">
                            {form.contact_person ? <>Attn: {F('contact_person')}</> : isEditing ? <><span className="text-slate-400 text-[10px]">Attn: </span>{F('contact_person')}</> : null}
                        </p>
                        <div className="h-px bg-slate-200 w-1/3 mb-3"></div>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed uppercase tracking-wider">
                            {F('customer_address')}
                        </p>
                    </div>

                    <div className="flex flex-col justify-center pl-8 border-l-2 border-slate-50">
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em] mb-3">Account Manager</p>
                        <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                            {F('executive_name')}
                        </h4>
                        <p className="text-[10px] font-black text-accent uppercase tracking-widest mt-1 mb-3">
                            {F('executive_designation')}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
                            <Mail size={12} className="text-primary/50 shrink-0" /> {F('executive_email')}
                        </div>
                    </div>
                </div>

                {/* 3. Items Table */}
                <div className="flex-grow overflow-hidden">
                    <table className="w-full border-separate border-spacing-0">
                        <thead>
                            <tr className="bg-slate-900 text-white">
                                <th className="p-3 text-center text-[10px] font-black uppercase tracking-widest rounded-tl-lg border-r border-slate-700 w-14">No.</th>
                                <th className="p-3 text-left text-[10px] font-black uppercase tracking-widest border-r border-slate-700">Description of Service/Product</th>
                                <th className="p-3 text-center text-[10px] font-black uppercase tracking-widest border-r border-slate-700 w-20">Qty</th>
                                <th className="p-3 text-right text-[10px] font-black uppercase tracking-widest border-r border-slate-700 w-28">Unit Price</th>
                                <th className="p-3 text-right text-[10px] font-black uppercase tracking-widest rounded-tr-lg w-36">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="border-x border-slate-900">
                            {form.items.map((item, idx) => (
                                <tr key={item.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                    <td className="p-5 text-center text-xs font-black text-slate-400 border-b border-l border-slate-200">{idx + 1}</td>
                                    <td className="p-5 border-b border-slate-200">
                                        {isEditing
                                            ? <>
                                                <EInput value={item.name} onChange={setItemField(idx, 'name')} style={{ fontWeight: 900, fontSize: '0.875rem', textTransform: 'uppercase', color: '#0f172a', marginBottom: 4 }} />
                                                <EInput value={item.description} onChange={setItemField(idx, 'description')} style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }} />
                                            </>
                                            : <>
                                                <p className="font-black text-slate-900 text-sm mb-1 uppercase tracking-tight">{item.name}</p>
                                                <p className="text-xs text-slate-500 font-medium leading-relaxed italic">{item.description}</p>
                                            </>
                                        }
                                    </td>
                                    <td className="p-5 text-center text-sm font-black text-slate-900 border-b border-slate-200">
                                        {isEditing
                                            ? <EInput value={item.qty} onChange={setItemField(idx, 'qty')} type="number" style={{ textAlign: 'center', fontWeight: 900 }} />
                                            : item.qty
                                        }
                                    </td>
                                    <td className="p-5 text-right text-sm font-black text-slate-900 border-b border-slate-200">
                                        {isEditing
                                            ? <EInput value={item.rate} onChange={setItemField(idx, 'rate')} type="number" style={{ textAlign: 'right', fontWeight: 900 }} />
                                            : formatCurrency(item.rate)
                                        }
                                    </td>
                                    <td className="p-5 text-right text-sm font-black text-primary border-b border-r border-slate-200">
                                        {formatCurrency(item.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 4. Totals and Summary */}
                <div className="mt-12 flex justify-between items-start break-inside-avoid">
                    <div className="w-[48%]">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Payment Terms &amp; Instructions</p>
                        <div className="bg-slate-50 p-6 rounded-2xl border-2 border-slate-100/50 text-[11px] font-bold text-slate-500 leading-relaxed italic">
                            {isEditing
                                ? <EArea value={form.terms} onChange={setField('terms')} rows={4} />
                                : <div className="whitespace-pre-line">{form.terms}</div>
                            }
                        </div>
                    </div>

                    <div className="w-[55%]">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-4">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Total Amount (Excl. VAT)</span>
                                <div className="flex items-baseline gap-1.5 ml-4">
                                    <span className="text-[10px] font-black opacity-40 italic">AED</span>
                                    <span className="font-black text-slate-900 text-lg">{formatCurrency(subtotal)}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center px-4">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">VAT Amount (5%)</span>
                                <div className="flex items-baseline gap-1.5 ml-4">
                                    <span className="text-[10px] font-black opacity-40 italic">AED</span>
                                    <span className="font-black text-slate-900 text-lg">{formatCurrency(vat)}</span>
                                </div>
                            </div>
                            <div className="bg-slate-900 px-6 py-6 rounded-3xl flex justify-between items-center text-white shadow-xl shadow-slate-900/10 min-h-[110px]">
                                <div className="pr-6 flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-60 mb-2 whitespace-nowrap">Grand Total</p>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-xs font-black opacity-60 italic shrink-0">AED</span>
                                        <p className="text-4xl font-black tracking-tighter leading-none italic">
                                            {formatCurrency(grandTotal)}
                                        </p>
                                    </div>
                                </div>
                                <div className="border-l border-white/10 pl-6 shrink-0 text-right">
                                    <span className="text-[8px] font-black uppercase tracking-[0.5em] block mb-1 opacity-60">Currency</span>
                                    <span className="text-[10px] font-black italic block leading-tight whitespace-nowrap">UAE<br />DIRHAM</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. Signatures */}
                <div className="mt-8 pt-8 grid grid-cols-2 gap-20 border-t-2 border-slate-50 break-inside-avoid">
                    <div className="text-center group">
                        <div className="h-20 border-b-2 border-slate-100 mb-4 bg-slate-50/20 transition-colors group-hover:bg-slate-50/40 rounded-t-3xl relative flex flex-col justify-end pb-3">
                            <p className="text-[10px] font-black text-slate-200 italic uppercase tracking-[0.2em] select-none">Official Signature</p>
                        </div>
                        <p className="font-black text-slate-900 text-[11px] uppercase tracking-tight">Authorised Signatory</p>
                        <p className="text-[10px] font-black text-primary uppercase tracking-widest mt-1 opacity-80 leading-none">
                            {F('company_name')}
                        </p>
                    </div>
                    <div className="text-center group">
                        <div className="h-20 border-b-2 border-slate-100 mb-4 bg-slate-50/20 transition-colors group-hover:bg-slate-50/40 rounded-t-3xl relative flex flex-col justify-end pb-3">
                            <p className="text-[10px] font-black text-slate-200 italic uppercase tracking-[0.2em] select-none">Stamp &amp; Date</p>
                        </div>
                        <p className="font-black text-slate-900 text-[11px] uppercase tracking-tight">{form.customer_name}</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1 opacity-80 leading-none">Acceptance of Quotation</p>
                    </div>
                </div>

                {/* 6. Page Footer */}
                <div className="mt-8 text-center">
                    <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em] leading-none">
                        {F('footer_note')}
                    </p>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    @page { size: A4; margin: 0; }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        height: 297mm !important;
                        width: 210mm !important;
                        overflow: hidden !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    /* Hide everything by default */
                    body * { visibility: hidden !important; }
                    /* Show only the print area and its children */
                    .print-area, .print-area * { visibility: visible !important; }
                    /* Position print area precisely at 0,0 */
                    .print-area {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: 210mm !important;
                        height: 297mm !important;
                        margin: 0 !important;
                        padding: 15mm !important; /* Internal content padding */
                        box-sizing: border-box !important;
                        background: white !important;
                        box-shadow: none !important;
                        display: flex !important;
                        flex-direction: column !important;
                        z-index: 9999 !important;
                    }
                    .print\\:hidden, aside, nav, button { display: none !important; }
                }
            `}} />
        </div>
    );
};

export default QuotationDetails;
